/**
 * ticket-submitter.ts — resolves "who submitted this ticket" for the Tickets
 * table and detail header.
 *
 * Attribution is spread across three columns because tickets arrive by three
 * different doors: the in-app form and the roadmap agent stamp
 * `createdByName` / `createdByEmail` from the JWT, a triager can name a
 * third party in `requestedBy`, and the tickets seeded from the docs backlog
 * (plus board backfills) carry only a `source`. Resolving the fallback chain
 * in one place keeps the table, the search index, and the detail page telling
 * the same story.
 */

import type {
  FeatureRequest,
  FeatureRequestCreatedVia,
  RoadmapPerson,
} from '@/api/endpoints/roadmap';
import { resolvePerson } from './person-match';

/** Wire values of `source` that are a fixed vocabulary; anything else is free text. */
const SOURCE_LABELS: Record<string, string> = {
  user: 'Internal user',
  client: 'Client',
  internal: 'Internal',
  management: 'Management',
};

export interface TicketSubmitter {
  /**
   * The person who filed the ticket — display name, else email. Null for
   * tickets seeded before per-user attribution existed.
   */
  person: string | null;
  /** Who it was filed on behalf of, when that differs from `person`. */
  requestedFor: string | null;
  /** Where the ticket came from. The only attribution the seeded rows have. */
  source: string | null;
}

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * How the ticket got here, when that's worth saying out loud. A form
 * submission is the unremarkable case and gets no label; the other two explain
 * why the row looks the way it does — 'agent' was typed at the chat, and
 * 'seed' is why an imported row has no person to name.
 */
export function ticketOriginLabel(createdVia: FeatureRequestCreatedVia): string | null {
  if (createdVia === 'agent') return 'via agent';
  if (createdVia === 'seed') return 'imported';
  return null;
}

/** Human label for a `source` value, passing free-text values through. */
export function ticketSourceLabel(source: string | null | undefined): string | null {
  const value = clean(source);
  if (!value) return null;
  return SOURCE_LABELS[value.toLowerCase()] ?? value;
}

/**
 * Resolves ticket attribution into the parts the UI renders.
 *
 * `person` prefers the display name over the email so the table stays
 * readable; `requestedFor` is omitted when it merely repeats `person`.
 */
export function ticketSubmitter(
  request: Pick<FeatureRequest, 'createdByName' | 'createdByEmail' | 'requestedBy' | 'source'>,
): TicketSubmitter {
  const createdBy = clean(request.createdByName) ?? clean(request.createdByEmail);
  const requestedBy = clean(request.requestedBy);
  const person = createdBy ?? requestedBy;
  const requestedFor =
    requestedBy && requestedBy.toLowerCase() !== person?.toLowerCase() ? requestedBy : null;

  return { person, requestedFor, source: ticketSourceLabel(request.source) };
}

/**
 * Flattens attribution into one string for table search and sorting. Includes
 * the email even when the display name won the label, so searching an address
 * finds the ticket.
 */
export function ticketSubmitterText(
  request: Pick<FeatureRequest, 'createdByName' | 'createdByEmail' | 'requestedBy' | 'source'>,
): string {
  const { person, requestedFor, source } = ticketSubmitter(request);
  return [person, clean(request.createdByEmail), requestedFor, source]
    .filter((part, index, parts): part is string => Boolean(part) && parts.indexOf(part) === index)
    .join(' ');
}

/**
 * One name for the table cell. Cramming "tfinigan · for Tara Finigan" into
 * the column is what overflowed into Type. Hover still has the full string
 * via {@link ticketSubmitterText}.
 */
export function ticketSubmitterLabel(
  request: Pick<
    FeatureRequest,
    'createdByName' | 'createdByEmail' | 'requestedBy' | 'source' | 'createdVia'
  >,
): string {
  const name = clean(request.createdByName);
  if (name) return name;
  const email = clean(request.createdByEmail);
  if (email) {
    const at = email.indexOf('@');
    return at > 0 ? email.slice(0, at) : email;
  }
  const requested = clean(request.requestedBy);
  if (requested) return requested;
  if (request.createdVia === 'seed') return 'Imported';
  return '—';
}

/**
 * Stable identity for the Submitted-by dropdown. Prefers the user id so a
 * rename does not split one person into two options, and includes the
 * snapshot email when there is one. The same numeric id is a different
 * person on local, stage, and prod, so id alone would merge them.
 */
export function ticketSubmitterKey(
  request: Pick<
    FeatureRequest,
    'createdByUserId' | 'createdByName' | 'createdByEmail' | 'requestedBy' | 'createdVia'
  >,
): string {
  const email = clean(request.createdByEmail)?.toLowerCase();
  if (request.createdByUserId != null) {
    return email ? `user:${request.createdByUserId}:${email}` : `user:${request.createdByUserId}`;
  }
  if (email) return `email:${email}`;
  const name = clean(request.createdByName);
  if (name) return `name:${name.toLowerCase()}`;
  const requested = clean(request.requestedBy);
  if (requested) return `requested:${requested.toLowerCase()}`;
  if (request.createdVia === 'seed') return 'imported';
  return 'unknown';
}

/**
 * Unique submitters on the loaded backlog, for the Submitted-by dropdown.
 * Imported / unknown rows sort last so named people stay easy to scan. When
 * the WS1 person roster is provided, labels resolve to canonical display names
 * — but only when that person's mailbox still matches the ticket snapshot.
 */
export function collectSubmitterOptions(
  tickets: readonly Pick<
    FeatureRequest,
    'createdByUserId' | 'createdByName' | 'createdByEmail' | 'requestedBy' | 'source' | 'createdVia'
  >[],
  people: readonly RoadmapPerson[] = [],
): Array<{ value: string; label: string }> {
  const byKey = new Map<string, string>();
  for (const ticket of tickets) {
    const key = ticketSubmitterKey(ticket);
    if (!byKey.has(key)) {
      const person = resolvePerson(
        people,
        ticket.createdByUserId,
        ticket.createdByName || ticket.createdByEmail,
        ticket.createdByEmail,
      );
      byKey.set(key, person?.displayName || ticketSubmitterLabel(ticket));
    }
  }
  return [...byKey.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      const aSpecial = a.value === 'imported' || a.value === 'unknown';
      const bSpecial = b.value === 'imported' || b.value === 'unknown';
      if (aSpecial !== bSpecial) return aSpecial ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
}
